import React, { useContext, useEffect, useState } from "react";

import { UserContext } from "../../contexts/UserContext";
import { mask, unMask } from "remask";
import { cpf, cnpj } from "cpf-cnpj-validator";

import { services } from "../../services/login";

import { Form, Input, notification, Button } from "antd";
import { UserOutlined, LockOutlined, MailOutlined } from "@ant-design/icons";

import style from "./Login.module.css";

function readLoginCompact() {
  if (typeof window === "undefined") return false;
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const screenMin = Math.min(window.screen.width, window.screen.height);
  return viewportW <= 1100 || viewportW < viewportH || screenMin <= 850;
}

const Login = () => {
  const { userLogin, loading, setLoading, error, setError, autoLogin } =
    useContext(UserContext);

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [form] = Form.useForm();
  const patterns = ["999.999.999-99", "99.999.999/9999-99"];
  const [hasForgotPassword, setHasForgotPassword] = useState(false);
  const [isCompact, setIsCompact] = useState(readLoginCompact);

  useEffect(() => {
    error && openNotificationWithIcon("error", <strong>{error}!</strong>);
  }, [error]);

  useEffect(() => {
    const update = () => setIsCompact(readLoginCompact());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  const onChange = (value) => {
    setLogin(unMask(value));
    form.setFieldsValue({
      login: mask(unMask(value), patterns),
    });
  };

  const handleMode = () => {
    setHasForgotPassword(!hasForgotPassword);
  };

  async function handleSubmit() {
    autoLogin();
    if (login.length <= 11) {
      if (cpf.isValid(form.getFieldValue("login")))
        await userLogin(login, password);
      else
        openNotificationWithIcon(
          "error",
          <strong>CPF inválido!</strong>,
          "CPF inválido pelo dígito verificador. Insira um CPF válido!"
        );
    } else {
      if (cnpj.isValid(form.getFieldValue("login"))) userLogin(login, password);
      else
        openNotificationWithIcon(
          "error",
          <strong>CNPJ inválido!</strong>,
          "CNPJ inválido pelo dígito verificador. Insira um CNPJ válido!"
        );
    }
  }

  async function handleSubmitForgotPassword(user) {
    setLoading(true);

    // Validação de CPF/CNPJ antes de enviar
    const loginValue = unMask(user.login);
    if (loginValue.length <= 11) {
      if (!cpf.isValid(user.login)) {
        openNotificationWithIcon(
          "error",
          <strong>CPF inválido!</strong>,
          "CPF inválido pelo dígito verificador. Insira um CPF válido!"
        );
        setLoading(false);
        return;
      }
    } else {
      if (!cnpj.isValid(user.login)) {
        openNotificationWithIcon(
          "error",
          <strong>CNPJ inválido!</strong>,
          "CNPJ inválido pelo dígito verificador. Insira um CNPJ válido!"
        );
        setLoading(false);
        return;
      }
    }

    try {
      await services.user.forgotPassword({
        login: loginValue,
        email: user.email,
      });

      setHasForgotPassword(false);

      openNotificationWithIcon(
        "success",
        <strong>Email de recuperação enviado!</strong>,
        <span>
          Um email de recuperação foi enviado para <strong>{email}</strong>.
          Verifique sua caixa de entrada e siga as instruções para redefinir sua
          senha.{" "}
        </span>
      );
    } catch (error) {
      const response = error?.response;
      const errorMessage =
        response?.data?.userMessage ||
        response?.data?.message ||
        "Erro ao enviar email de recuperação";
      const errorTitle = response?.data?.title || "Erro";

      // Verifica se a mensagem de erro é sobre credenciais inválidas
      // Nesse caso, o email pode ter sido enviado mesmo assim (comportamento do backend)
      const isInvalidCredentials =
        errorMessage?.toLowerCase().includes("usuário") ||
        errorMessage?.toLowerCase().includes("senha") ||
        errorMessage?.toLowerCase().includes("inválido") ||
        errorMessage?.toLowerCase().includes("invalid");

      if (isInvalidCredentials) {
        // Se for erro com mensagem de credenciais inválidas,
        // pode ser que o email tenha sido enviado mesmo assim
        // Mostramos uma mensagem mais amigável sugerindo verificar o email
        openNotificationWithIcon(
          "warning",
          <strong>Atenção!</strong>,
          <span>
            {errorMessage}. Se o CPF e email estiverem corretos, verifique sua
            caixa de entrada. Caso não receba o email, entre em contato com o
            suporte.
          </span>
        );
      } else {
        // Outros erros são tratados normalmente
        openNotificationWithIcon(
          "error",
          <strong>{errorTitle}</strong>,
          errorMessage
        );
      }
    } finally {
      setLoading(false);
      setError("");
    }
  }

  const openNotificationWithIcon = (type, message, description) => {
    notification[type]({
      message: message,
      description: description,
      duration: 10,
      style: {
        width: 900,
        background: type === "error" ? "#FFCBCB" : "#f6ffed",
      },
    });
  };

  const publico = process.env.PUBLIC_URL || "";
  const fundoLogin = `${publico}/login-fundo.jpg?v=8`;

  return (
    <div
      className={`${style.page} ${isCompact ? style.isCompact : ""}`}
      style={{ "--login-fundo": `url(${fundoLogin})` }}
      title={process.env.REACT_APP_VERSION}
    >
      <div className={style.stage}>
        {process.env.REACT_APP_ENV !== "production" && (
          <div className={style.homolog}>Ambiente de homologação</div>
        )}
        <div className={style.panel}>
          <div className={style.compactHeader}>
            <img
              className={style.brand}
              src={`${publico}/oisol200x69.png`}
              alt="Oisol"
            />
            <h1 className={style.welcome}>
              {hasForgotPassword
                ? "Recuperação de senha do Sistema do Observatório Estadual de Direitos Humanos"
                : "Bem vindo(a) ao Sistema do Observatório Estadual de Direitos Humanos"}
            </h1>
          </div>
      <Form
        onFinish={
          hasForgotPassword ? handleSubmitForgotPassword : handleSubmit
        }
        className={style.form}
        requiredMark={false}
        form={form}
        name="loginForm"
        layout="vertical"
      >
        <span className={`${style.glyph} ${style.loginGlyph}`} aria-hidden="true">
          <UserOutlined />
        </span>
        <Form.Item
          className={`${style.field} ${style.loginField}`}
          name="login"
          rules={[{ required: true, message: "Este campo é obrigatório!" }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="login"
            aria-label="Login. O login pode ser um CPF ou um CNPJ."
            id="login"
            onChange={(e) => onChange(e.target.value)}
          />
        </Form.Item>

        {hasForgotPassword ? (
          <>
          <span className={`${style.glyph} ${style.secretGlyph}`} aria-hidden="true">
            <MailOutlined />
          </span>
          <Form.Item
            className={`${style.field} ${style.secretField}`}
            name="email"
            rules={[
              { required: true, message: "Este campo é obrigatório!" },
              { type: "email", message: "Insira um email válido" },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="e-mail"
              aria-label="E-mail"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Form.Item>
          </>
        ) : (
          <>
          <span className={`${style.glyph} ${style.secretGlyph}`} aria-hidden="true">
            <LockOutlined />
          </span>
          <Form.Item
            className={`${style.field} ${style.secretField}`}
            name="password"
            rules={[{ required: true, message: "Este campo é obrigatório!" }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Senha"
              aria-label="Senha"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Item>
          </>
        )}

        <p className={style.recoverLine}>
          {hasForgotPassword ? (
            <button type="button" onClick={handleMode}>
              Ir para login
            </button>
          ) : (
            <>
              Esqueceu a senha?{" "}
              <button type="button" onClick={handleMode}>
                Recupere o acesso
              </button>
            </>
          )}
        </p>

        <Button
          className={style.submit}
          htmlType="submit"
          type="primary"
          loading={loading}
        >
          {hasForgotPassword ? "Recuperar" : "Entrar"}
        </Button>
        <img
          className={style.seal}
          src={`${publico}/logoSecDH_500x163.png`}
          alt="Governo do Estado do Ceará — Secretaria dos Direitos Humanos"
        />
      </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;
